// src/components/product/ProductVariantSection.tsx
import * as React from "react";
import { Typography, Button, Input, InputNumber, Switch, Radio } from "antd";
import { PlusOutlined, CloseOutlined, DeleteOutlined } from "@ant-design/icons";
import type {
  Variant,
  OptionSelectionType,
  OptionGroupCreate,
  OptionGroupUpdate,
} from "src/types/product";
import { snackbarStore } from "src/store/snackbarStore";

const toast = (message: string, severity: "success" | "error" | "info" | "warning" = "info") =>
  snackbarStore.getState().addSnackbar({ message, severity });

// 메모이제이션된 Variant Row (변경된 행만 리렌더링)
const VariantRow = React.memo(
  function VariantRow({
    variant,
    index,
    optionGroups,
    onUpdate,
    onRemove,
  }: {
    variant: Variant;
    index: number;
    optionGroups: OptionGroupCreate[] | OptionGroupUpdate[];
    onUpdate: <K extends keyof Variant>(index: number, field: K, value: Variant[K]) => void;
    onRemove: (index: number) => void;
  }) {
    const values = variant.variantName?.split("+") || [];

    const cellStyle: React.CSSProperties = {
      padding: "8px",
      borderBottom: "1px solid #f0f0f0",
      textAlign: "center",
    };

    return (
      <tr style={{ backgroundColor: index % 2 === 0 ? "#fff" : "#fafafa" }}>
        {optionGroups.map((_, idx) => (
          <td key={idx} style={cellStyle}>
            <Typography.Text>{values[idx] || "-"}</Typography.Text>
          </td>
        ))}
        <td style={cellStyle}>
          <Button size="small" type="link">
            {variant.skuMapped ? "연결됨" : "연결안됨"}
          </Button>
        </td>
        <td style={{ ...cellStyle, textAlign: "left" }}>
          <LocalInputNumber
            value={variant.optionPrice}
            onChange={(val) => onUpdate(index, "optionPrice", val)}
            min={0}
            prefix="+"
            style={{ width: "100%" }}
          />
        </td>
        <td style={cellStyle}>
          <Typography.Text>{variant.isSoldout ? "품절" : "판매중"}</Typography.Text>
        </td>
        <td style={{ ...cellStyle, textAlign: "left" }}>
          <LocalInput
            value={variant.variantCode || ""}
            onChange={(val) => onUpdate(index, "variantCode", val)}
            size="small"
            placeholder="ex) SP04987"
          />
        </td>
        <td style={cellStyle}>
          <Typography.Text>{variant.sellableQuantity?.toLocaleString() || 0}개</Typography.Text>
        </td>
        <td style={cellStyle}>
          <Switch
            checked={variant.isActive}
            onChange={(checked) => onUpdate(index, "isActive", checked)}
          />
        </td>
        <td style={cellStyle}>
          <Button
            type="text"
            icon={<DeleteOutlined />}
            onClick={() => onRemove(index)}
          />
        </td>
      </tr>
    );
  },
  (prevProps, nextProps) => {
    // variant 객체가 같으면 리렌더링 스킵
    return (
      prevProps.variant === nextProps.variant &&
      prevProps.index === nextProps.index &&
      prevProps.optionGroups === nextProps.optionGroups
    );
  }
);

// InputNumber 컴포넌트 (React.memo만 사용)
const LocalInputNumber = React.memo(function LocalInputNumber({
  value,
  onChange,
  min,
  prefix,
  style,
}: {
  value: number;
  onChange: (val: number) => void;
  min?: number;
  prefix?: string;
  style?: React.CSSProperties;
}) {
  return (
    <InputNumber
      value={value}
      onChange={(val) => onChange(val || 0)}
      min={min}
      prefix={prefix}
      style={style}
    />
  );
});

// Input 컴포넌트 (React.memo만 사용)
const LocalInput = React.memo(function LocalInput({
  value,
  onChange,
  size,
  placeholder,
}: {
  value: string;
  onChange: (val: string) => void;
  size?: "small" | "middle" | "large";
  placeholder?: string;
}) {
  return (
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      size={size}
      placeholder={placeholder}
    />
  );
});

type Props = {
  initialValue: Variant[];
  valueRef: React.MutableRefObject<Variant[]>;
  optionSelectionType: OptionSelectionType;
  onOptionSelectionTypeChange: (type: OptionSelectionType) => void;
  optionGroups: OptionGroupCreate[] | OptionGroupUpdate[];
  onOptionGroupsChange: (groups: OptionGroupCreate[] | OptionGroupUpdate[]) => void;
  isUpdate?: boolean; // Update 모드인지 여부
};

type OptionInputRow = {
  id: string;
  groupName: string;
  optionValues: string; // 콤마로 구분된 값
};

export default function ProductVariantSection({
  initialValue,
  valueRef,
  optionSelectionType,
  onOptionSelectionTypeChange,
  optionGroups,
  onOptionGroupsChange,
  isUpdate = false,
}: Props) {
  // 내부에서 variants 상태 관리 (부모 리렌더링 방지)
  const [variants, setVariants] = React.useState<Variant[]>(initialValue);

  // valueRef에 항상 최신값 동기화
  React.useEffect(() => {
    valueRef.current = variants;
  }, [variants, valueRef]);

  const [inputRows, setInputRows] = React.useState<OptionInputRow[]>([
    { id: "1", groupName: "", optionValues: "" },
  ]);
  const [isInitialized, setIsInitialized] = React.useState(false);

  // 기존 데이터에서 inputRows 초기화 (최초 1회만)
  React.useEffect(() => {
    if (!isInitialized && optionGroups.length > 0 && inputRows.length === 1 && !inputRows[0].groupName) {
      const newInputRows: OptionInputRow[] = optionGroups.map((group, index) => {
        let optionValues = "";
        if ("optionValueNames" in group) {
          // Create 모드
          optionValues = group.optionValueNames.join(",");
        } else if ("optionValues" in group) {
          // Update 모드
          optionValues = group.optionValues.map((v) => v.valueName).join(",");
        }
        return {
          id: (index + 1).toString(),
          groupName: group.groupName,
          optionValues,
        };
      });

      if (newInputRows.length > 0) {
        setInputRows(newInputRows);
        setIsInitialized(true);
      }
    }
  }, [optionGroups, inputRows, isInitialized]);

  const addInputRow = () => {
    if (inputRows.length >= 3) {
      toast("옵션은 최대 3개까지만 추가할 수 있습니다.", "warning");
      return;
    }
    setInputRows([...inputRows, { id: Date.now().toString(), groupName: "", optionValues: "" }]);
  };

  const removeInputRow = (id: string) => {
    setInputRows(inputRows.filter((r) => r.id !== id));
  };

  const updateInputRow = (id: string, field: keyof OptionInputRow, newValue: string) => {
    setInputRows(inputRows.map((r) => (r.id === id ? { ...r, [field]: newValue } : r)));
  };

  // 카르테시안 곱을 계산하는 함수
  const cartesianProduct = (arrays: string[][]): string[][] => {
    if (arrays.length === 0) return [[]];
    if (arrays.length === 1) return arrays[0].map((v) => [v]);

    const [first, ...rest] = arrays;
    const restProduct = cartesianProduct(rest);
    const result: string[][] = [];

    for (const item of first) {
      for (const subArray of restProduct) {
        result.push([item, ...subArray]);
      }
    }

    return result;
  };

  const handleGenerate = () => {
    const validRows = inputRows.filter((r) => r.groupName.trim() && r.optionValues.trim());
    if (validRows.length === 0) {
      toast("옵션 그룹과 옵션 값을 입력해주세요.", "warning");
      return;
    }

    // OptionGroups 생성
    if (isUpdate) {
      // Update 모드: 기존 optionGroups의 ID를 유지하면서 업데이트
      const updatedGroups: OptionGroupUpdate[] = validRows.map((row, index) => {
        const existingGroup = optionGroups[index] as OptionGroupUpdate | undefined;
        const values = row.optionValues
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean);

        const optionValues: any[] = values.map((valueName, vIndex) => {
          // 기존 값과 매칭
          const existingValue =
            existingGroup && "optionValues" in existingGroup
              ? existingGroup.optionValues.find((v) => v.valueName === valueName)
              : undefined;

          return {
            optionValueId: existingValue?.optionValueId || null,
            valueName,
            displayOrder: vIndex,
          };
        });

        return {
          optionGroupId: existingGroup?.optionGroupId || null,
          groupName: row.groupName.trim(),
          displayOrder: index,
          optionValues,
        };
      });
      onOptionGroupsChange(updatedGroups);
    } else {
      // Create 모드
      const newGroups: OptionGroupCreate[] = validRows.map((row, index) => ({
        groupName: row.groupName.trim(),
        displayOrder: index,
        optionValueNames: row.optionValues
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean),
      }));
      onOptionGroupsChange(newGroups);
    }

    if (optionSelectionType === "INDEPENDENT") {
      // INDEPENDENT: 카르테시안 곱으로 조합 생성
      const valueArrays = validRows.map((row) =>
        row.optionValues
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean)
      );

      const valueCombinations = cartesianProduct(valueArrays);

      const generated: Variant[] = valueCombinations.map((combination, index) => {
        const variantName = combination.join("+");
        const optionCombination = combination.map((_, idx) =>
          valueArrays[idx].indexOf(combination[idx])
        );

        // 기존 variant가 있으면 유지
        const existingVariant = variants.find((v: Variant) => v.variantName === variantName);

        // Update 모드일 때 optionValueIds 생성
        let optionValueIds: number[] | undefined;
        if (isUpdate && optionGroups.length > 0) {
          optionValueIds = combination.map((valueName, groupIdx) => {
            const group = optionGroups[groupIdx] as any;
            if (group && "optionValues" in group) {
              const optionValue = group.optionValues.find(
                (v: any) => v.valueName === valueName
              );
              return optionValue?.optionValueId || 0;
            }
            return 0;
          });
        }

        return {
          variantId: existingVariant?.variantId || null,
          variantCode: existingVariant?.variantCode || "",
          variantName,
          optionCombination: !isUpdate ? optionCombination : undefined,
          optionValueIds: isUpdate ? optionValueIds : undefined,
          optionPrice: existingVariant?.optionPrice || 0,
          isSoldout: existingVariant?.isSoldout || false,
          isActive: existingVariant?.isActive ?? true,
          skuMapped: existingVariant?.skuMapped || false,
          displayOrder: index,
          sellableQuantity: existingVariant?.sellableQuantity,
        };
      });

      setVariants(generated);
    } else {
      // COMBINATION: 카르테시안 곱으로 모든 조합 생성 (선택형과 동일)
      // 차이점은 프론트엔드 UI에서 드롭다운이 아닌 목록으로 표시됨
      const valueArrays = validRows.map((row) =>
        row.optionValues
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean)
      );

      const valueCombinations = cartesianProduct(valueArrays);

      const generated: Variant[] = valueCombinations.map((combination, index) => {
        const variantName = combination.join("+");
        const optionCombination = combination.map((_, idx) =>
          valueArrays[idx].indexOf(combination[idx])
        );

        // 기존 variant가 있으면 유지
        const existingVariant = variants.find((v: Variant) => v.variantName === variantName);

        // Update 모드일 때 optionValueIds 생성
        let optionValueIds: number[] | undefined;
        if (isUpdate && optionGroups.length > 0) {
          optionValueIds = combination.map((valueName, groupIdx) => {
            const group = optionGroups[groupIdx] as any;
            if (group && "optionValues" in group) {
              const optionValue = group.optionValues.find(
                (v: any) => v.valueName === valueName
              );
              return optionValue?.optionValueId || 0;
            }
            return 0;
          });
        }

        return {
          variantId: existingVariant?.variantId || null,
          variantCode: existingVariant?.variantCode || "",
          variantName,
          optionCombination: !isUpdate ? optionCombination : undefined,
          optionValueIds: isUpdate ? optionValueIds : undefined,
          optionPrice: existingVariant?.optionPrice || 0,
          isSoldout: existingVariant?.isSoldout || false,
          isActive: existingVariant?.isActive ?? true,
          skuMapped: existingVariant?.skuMapped || false,
          displayOrder: index,
          sellableQuantity: existingVariant?.sellableQuantity,
        };
      });

      setVariants(generated);
    }

    toast("옵션이 적용되었습니다.", "success");
  };

  const variantsRef = React.useRef(variants);
  React.useEffect(() => {
    variantsRef.current = variants;
  }, [variants]);

  const updateVariant = <K extends keyof Variant>(
    index: number,
    field: K,
    newValue: Variant[K]
  ) => {
    const updated = [...variantsRef.current];
    updated[index] = { ...updated[index], [field]: newValue };
    setVariants(updated);
  };

  const removeVariant = (index: number) => {
    setVariants(variantsRef.current.filter((_, i) => i !== index));
  };

  const updateVariantRef = React.useRef(updateVariant);
  const removeVariantRef = React.useRef(removeVariant);

  React.useEffect(() => {
    updateVariantRef.current = updateVariant;
    removeVariantRef.current = removeVariant;
  });

  return (
    <div>
      <Typography.Title level={5}>옵션 유형 선택</Typography.Title>
      <Radio.Group
        value={optionSelectionType}
        onChange={(e) => onOptionSelectionTypeChange(e.target.value)}
        style={{ marginBottom: 24 }}
      >
        <Radio.Button value="INDEPENDENT">
          선택형 (드롭다운 조합)
        </Radio.Button>
        <Radio.Button value="COMBINATION">
          조합형 (미리 조합된 목록)
        </Radio.Button>
      </Radio.Group>

      <Typography.Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
        {optionSelectionType === "INDEPENDENT"
          ? "선택형: 여러 옵션 그룹의 값들이 자동으로 조합되어 드롭다운 형태로 제공됩니다. (예: 제품 x 부재료)"
          : "조합형: 미리 조합된 목록 형태로 제공됩니다. 자동 생성된 조합 중 불필요한 항목은 삭제할 수 있습니다."}
      </Typography.Text>

      <Typography.Title level={5}>옵션입력</Typography.Title>
      <Typography.Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
        옵션값을 콤마(,)로 구분하여 입력하세요. 모든 옵션의 조합이 자동으로 생성됩니다.
        {optionSelectionType === "COMBINATION" && " (조합형에서는 필요없는 조합을 삭제할 수 있습니다)"}
      </Typography.Text>

      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
        {inputRows.map((row, index) => (
          <div
            key={row.id}
            style={{ display: "flex", alignItems: "flex-end", gap: 8, flexWrap: "wrap" }}
          >
            <div style={{ minWidth: 150, flex: 1 }}>
              {index === 0 && (
                <Typography.Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
                  옵션 그룹명
                </Typography.Text>
              )}
              <Input
                value={row.groupName}
                onChange={(e) => updateInputRow(row.id, "groupName", e.target.value)}
                placeholder="ex) 색상, 사이즈, 제품"
              />
            </div>
            <div style={{ minWidth: 200, flex: 2 }}>
              {index === 0 && (
                <Typography.Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
                  옵션값
                </Typography.Text>
              )}
              <Input
                value={row.optionValues}
                onChange={(e) => updateInputRow(row.id, "optionValues", e.target.value)}
                placeholder="빨강,파랑,노랑 (,단위로 분기)"
              />
            </div>
            <div style={{ display: "flex", gap: 4, flexShrink: 0, minWidth: 220 }}>
              <Button
                icon={<CloseOutlined />}
                onClick={() => removeInputRow(row.id)}
                disabled={inputRows.length <= 1}
              />
              {index === inputRows.length - 1 ? (
                <>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={addInputRow}
                    disabled={inputRows.length >= 3}
                  />
                  <Button
                    onClick={handleGenerate}
                    style={{ backgroundColor: "#52c41a", color: "white", border: "none" }}
                  >
                    옵션 적용
                  </Button>
                </>
              ) : (
                <>
                  <div style={{ width: 32 }} />
                  <div style={{ width: 88 }} />
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
        옵션목록 ({variants.length}개)
      </Typography.Text>

      {variants.length > 0 ? (
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              minWidth: 1100,
              borderCollapse: "collapse",
              border: "1px solid #f0f0f0",
              fontSize: 14,
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#fafafa" }}>
                {optionGroups.map((group, idx) => (
                  <th
                    key={idx}
                    style={{
                      padding: "12px 8px",
                      borderBottom: "1px solid #f0f0f0",
                      fontWeight: 500,
                      textAlign: "center",
                      minWidth: 100,
                    }}
                  >
                    {group.groupName}
                  </th>
                ))}
                <th style={{ padding: "12px 8px", borderBottom: "1px solid #f0f0f0", textAlign: "center", minWidth: 100 }}>
                  SKU 연결
                </th>
                <th style={{ padding: "12px 8px", borderBottom: "1px solid #f0f0f0", textAlign: "center", minWidth: 130 }}>
                  옵션가
                </th>
                <th style={{ padding: "12px 8px", borderBottom: "1px solid #f0f0f0", textAlign: "center", minWidth: 80 }}>
                  판매상태
                </th>
                <th style={{ padding: "12px 8px", borderBottom: "1px solid #f0f0f0", textAlign: "center", minWidth: 150 }}>
                  관리코드
                </th>
                <th style={{ padding: "12px 8px", borderBottom: "1px solid #f0f0f0", textAlign: "center", minWidth: 100 }}>
                  재고수량
                </th>
                <th style={{ padding: "12px 8px", borderBottom: "1px solid #f0f0f0", textAlign: "center", minWidth: 80 }}>
                  사용여부
                </th>
                <th style={{ padding: "12px 8px", borderBottom: "1px solid #f0f0f0", textAlign: "center", minWidth: 60 }}>
                  삭제
                </th>
              </tr>
            </thead>
            <tbody>
              {variants.map((variant: Variant, index: number) => (
                <VariantRow
                  key={variant.variantId?.toString() || `variant-${index}`}
                  variant={variant}
                  index={index}
                  optionGroups={optionGroups}
                  onUpdate={updateVariantRef.current}
                  onRemove={removeVariantRef.current}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div
          style={{
            border: "1px solid #d9d9d9",
            borderRadius: 4,
            padding: 40,
            textAlign: "center",
            color: "#999",
          }}
        >
          옵션을 입력하고 "옵션 적용" 버튼을 눌러주세요.
        </div>
      )}
    </div>
  );
}
